import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextField, Button, Box, Typography, Alert, Stack, IconButton, InputAdornment, CircularProgress } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { OFFICIAL_TOURNAMENTS_PATH, useAuth } from '../../contexts/AuthContext';
import { appleTokens as t } from '../../theme/appleTokens';
import { clubLogoUrl, useClubPublicConfig } from '../../services/clubPublicConfig';

/**
 * Connexion à l'ERP (staff du club). Grand écran : panneau foncé du menu de l'ERP (logo blanc du club, nom, liseré à la
 * couleur du club) à gauche, formulaire à droite ; mobile : logo au-dessus du formulaire. Couleurs du kit (`appleTokens`).
 */
const LoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, authNotice, clearAuthNotice } = useAuth();
  const clubConfig = useClubPublicConfig();
  const clubName = clubConfig['club.name'] || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');

    if (!email || !password) {
      setError('Renseigne ton e-mail et ton mot de passe.');
      return;
    }

    setLoading(true);
    const result = await login(email, password);
    setLoading(false);

    if (!result.success) {
      setError(result.error || 'Identifiants invalides');
      setPassword('');
      return;
    }
    // Connecté à un autre club que celui affiché : rechargement complet sur ce club (config, sports, terrains).
    if (result.reloadUrl) {
      window.location.assign(result.reloadUrl);
      return;
    }
    // Accueil du rôle (staff de tournoi officiel : « Mes tournois officiels »), écran Matchs sinon.
    navigate(result.homePath === OFFICIAL_TOURNAMENTS_PATH ? result.homePath : '/app/matches', { replace: true });
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: t.canvas, display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          width: '100%',
          maxWidth: { xs: 420, md: 880 },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          bgcolor: t.surface,
          border: `1px solid ${t.separator}`,
          borderRadius: t.radius.xl,
          boxShadow: t.shadow.raised,
          overflow: 'hidden',
        }}
      >
        {/* Marque du club (grand écran). */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            p: 5,
            position: 'relative',
            bgcolor: t.sidebar.bg,
            minHeight: 460,
          }}
        >
          <Box component="img" src={clubLogoUrl(clubConfig, 'white')} alt={clubName} sx={{ height: 80, width: 'auto', maxWidth: '75%', objectFit: 'contain' }} />
          {clubName ? <Typography sx={{ ...t.type.body, color: t.sidebar.text, textAlign: 'center' }}>{clubName}</Typography> : null}
          {/* La couleur du club en simple liseré, en bas du panneau. */}
          <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 4, bgcolor: t.accent }} />
        </Box>

        <Box sx={{ p: { xs: 3, sm: 5 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Box
            component="img"
            src={clubLogoUrl(clubConfig, 'black')}
            alt={clubName}
            sx={{ display: { xs: 'block', md: 'none' }, height: 44, width: 'auto', maxWidth: 200, objectFit: 'contain', mx: 'auto', mb: 3 }}
          />
          <Typography component="h1" sx={{ ...t.type.pageTitle, color: t.label, mb: 3 }}>
            Connexion
          </Typography>

          {authNotice && !error ? (
            // Déconnexion forcée au démarrage (ex. accès au tournoi officiel terminés).
            <Alert severity="info" sx={{ mb: 2 }} onClose={clearAuthNotice}>
              {authNotice}
            </Alert>
          ) : null}
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Stack component="form" onSubmit={handleSubmit} spacing={2} noValidate>
            <TextField
              fullWidth
              label="E-mail"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              autoFocus
            />
            <TextField
              fullWidth
              label="Mot de passe"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        edge="end"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                      >
                        {showPassword ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Button variant="contained" fullWidth size="large" type="submit" disabled={loading} sx={{ mt: 1 }}>
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Se connecter'}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
